import React, { useState, useEffect } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import styled, { css } from 'styled-components';
import useLMSAuth from '../hooks/useLMSAuth';
import { ssoLogout } from '../../Common/Utils/SsoSession';

const MENU_ITEMS = [
  { name: '강사소개', path: '/Instructors' },
  { name: '수업과정', path: '/Courses' },
  { name: '수강신청', path: '/Enroll' },
  { name: '내 수업', path: '/MyClassroom' },
  { name: '커뮤니티', path: '/NewsBoard/0' },
  { name: '고객센터', path: '/Help' },
];
// 자료실은 라우트만 유지하고 헤더에서는 숨긴다. 내 수업은 기존 자료실 위치에 노출한다.

const HeaderOuter = styled.header`
  position: sticky;
  top: 0;
  width: 100%;
  z-index: ${({ theme }) => theme.zIndex.sticky};
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutral[100]};
  box-shadow: ${({ $scrolled, theme }) => ($scrolled ? theme.elevation.md : 'none')};
`;

const HeaderInner = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.layout.maxWidth};
  margin: 0 auto;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  box-sizing: border-box;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 0 16px;
    height: 64px;
  }
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  flex-shrink: 0;
`;

const LogoIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: linear-gradient(135deg, #005c68, #00bed8);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 900;
  font-size: 0.95rem;
`;

const LogoText = styled.span`
  font-family: ${({ theme }) => theme.typography.fontFamily.bold};
  font-weight: 900;
  font-size: 1.05rem;
  color: ${({ theme }) => theme.colors.neutral[900]};
  line-height: 1.15;

  small {
    display: block;
    font-family: ${({ theme }) => theme.typography.fontFamily.primary};
    font-weight: 500;
    font-size: 0.68rem;
    color: ${({ theme }) => theme.colors.neutral[400]};
    text-transform: uppercase;
    margin-top: 2px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.95rem;
    small {
      display: none;
    }
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 4px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    position: fixed;
    top: 0;
    right: 0;
    width: min(300px, 82vw);
    height: 100vh;
    background: ${({ theme }) => theme.colors.neutral[0]};
    box-shadow: ${({ $open }) => ($open ? '-8px 0 30px rgba(0,0,0,0.12)' : 'none')};
    transform: translateX(${({ $open }) => ($open ? '0' : '100%')});
    transition: transform 0.25s ease;
    flex-direction: column;
    align-items: stretch;
    padding: 78px 0 24px;
    z-index: 10000;
  }
`;

const NavItem = styled(Link)`
  display: flex;
  align-items: center;
  min-height: 38px;
  padding: 0 12px;
  color: ${({ theme }) => theme.colors.neutral[700]};
  -webkit-text-fill-color: ${({ theme }) => theme.colors.neutral[700]};
  font-size: 0.9rem;
  font-weight: 700;
  text-decoration: none;
  border-radius: 8px;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.colors.primary[700]};
    -webkit-text-fill-color: ${({ theme }) => theme.colors.primary[700]};
    background: ${({ theme }) => theme.colors.primary[50]};
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      color: ${theme.colors.primary[700]};
      -webkit-text-fill-color: ${theme.colors.primary[700]};
      background: ${theme.colors.primary[50]};
    `}

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    min-height: 48px;
    padding: 0 24px;
    border-radius: 0;
    border-bottom: 1px solid ${({ theme }) => theme.colors.neutral[100]};
  }
`;

const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 14px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const AuthButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
  padding: 0 16px;
  font-size: 0.88rem;
  font-weight: 800;
  border-radius: 8px;
  text-decoration: none;

  ${({ $variant, theme }) =>
    $variant === 'primary'
      ? css`
          background: ${theme.colors.primary[700]};
          color: #fff;
          -webkit-text-fill-color: #fff;
        `
      : css`
          background: #fff;
          color: ${theme.colors.neutral[700]};
          -webkit-text-fill-color: ${theme.colors.neutral[700]};
          border: 1px solid ${theme.colors.neutral[200]};
        `}
`;

const MobileAuthGroup = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 18px 24px;
  }
`;

const Hamburger = styled.button`
  display: none;
  width: 42px;
  height: 42px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  z-index: 10001;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 5px;
  }
`;

const HamburgerLine = styled.span`
  width: 21px;
  height: 2px;
  background: ${({ theme }) => theme.colors.neutral[800]};
  border-radius: 2px;
  transition: transform 0.2s ease, opacity 0.2s ease;

  ${({ $open }) =>
    $open &&
    css`
      &:nth-child(1) {
        transform: translateY(7px) rotate(45deg);
      }
      &:nth-child(2) {
        opacity: 0;
      }
      &:nth-child(3) {
        transform: translateY(-7px) rotate(-45deg);
      }
    `}
`;

const Overlay = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.42);
    opacity: ${({ $open }) => ($open ? 1 : 0)};
    visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
    z-index: 9999;
  }
`;

const MobileNavHeader = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 64px;
    padding: 0 20px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.neutral[100]};
  }
`;

const CloseButton = styled.button`
  width: 34px;
  height: 34px;
  border: 0;
  background: ${({ theme }) => theme.colors.neutral[100]};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.neutral[800]};
  cursor: pointer;
`;

const UserChip = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
  padding: 0 12px 0 8px;
  background: ${({ theme }) => theme.colors.primary[50] || '#ecfeff'};
  color: ${({ theme }) => theme.colors.primary[800] || '#0e7490'};
  -webkit-text-fill-color: ${({ theme }) => theme.colors.primary[800] || '#0e7490'};
  border-radius: 999px;
  text-decoration: none;
  font-weight: 800;
  font-size: 0.85rem;
  border: 1px solid ${({ theme }) => theme.colors.primary[200] || '#a5f3fc'};

  &:hover { background: ${({ theme }) => theme.colors.primary[100] || '#cffafe'}; }
`;
const Avatar = styled.span`
  width: 26px; height: 26px; border-radius: 50%;
  background: linear-gradient(135deg, #0e7490, #0891b2);
  color: #fff; -webkit-text-fill-color: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  font-weight: 900; font-size: 0.8rem;
`;
const RoleTag = styled.span`
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${({ $role }) => $role === 'admin' ? '#fef3c7' : $role === 'instructor' ? '#dcfce7' : '#e0f2fe'};
  color: ${({ $role }) => $role === 'admin' ? '#b45309' : $role === 'instructor' ? '#166534' : '#075985'};
  -webkit-text-fill-color: ${({ $role }) => $role === 'admin' ? '#b45309' : $role === 'instructor' ? '#166534' : '#075985'};
  font-weight: 800;
`;
const LogoutTextBtn = styled.button`
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  color: ${({ theme }) => theme.colors.neutral[600]};
  padding: 0 12px;
  min-height: 38px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  &:hover { color: #b91c1c; border-color: #fecaca; background: #fef2f2; }
`;

function Header() {
  const location = useLocation();
  const history = useHistory();
  const { login, name, user_id, role, refresh } = useLMSAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    if (!window.confirm('로그아웃 하시겠습니까?')) return;
    await ssoLogout();
    // .antsnest.co.kr 공유 쿠키가 삭제되어 모든 SSO 서비스에서 동시 로그아웃됨
    window.location.href = '/';
  };

  const initial = (name || user_id || '?').slice(0, 1).toUpperCase();
  const roleLabel = role === 'admin' ? '관리자' : role === 'instructor' ? '강사' : '수강생';

  return (
    <HeaderOuter $scrolled={scrolled}>
      <HeaderInner>
        <LogoLink to="/">
          <LogoIcon>AN</LogoIcon>
          <LogoText>
            ANTSNEST
            <small>Developer Center</small>
          </LogoText>
        </LogoLink>

        <Overlay $open={menuOpen} onClick={() => setMenuOpen(false)} />

        <Nav $open={menuOpen} aria-label="주요 메뉴">
          <MobileNavHeader>
            <strong>메뉴</strong>
            <CloseButton onClick={() => setMenuOpen(false)} aria-label="메뉴 닫기">X</CloseButton>
          </MobileNavHeader>
          {MENU_ITEMS.map((item) => {
            // 게시판 메뉴는 4종 라우트 모두 활성으로 인식
            const isBoardMenu = item.path.startsWith('/NewsBoard');
            const isOnBoard = isBoardMenu && /^\/(News|Free|Qa|DeveloperNote)Board(\/|$)/.test(location.pathname);
            const active = isOnBoard || location.pathname === item.path;
            return (
              <NavItem
                key={item.path}
                to={item.path}
                $active={active}
              >
                {item.name}
              </NavItem>
            );
          })}
          <MobileAuthGroup>
            {login ? (
              <>
                {(role === 'admin' || role === 'instructor') && (
                  <AuthButton to="/Enroll?tab=manage">수강 승인</AuthButton>
                )}
                {/* 봇이 매일 채우는 강의를 사람이 여기서 본다. 주소를
                    외워야만 들어갈 수 있으면 아무도 안 들어간다. */}
                {role === 'admin' && (
                  <AuthButton to="/Admin/DailyLectures">강의 관제</AuthButton>
                )}
                <AuthButton to="/MyPage" $variant="primary">{name || user_id} · 마이페이지</AuthButton>
                <AuthButton as="button" onClick={handleLogout} style={{ background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca', WebkitTextFillColor: '#b91c1c' }}>로그아웃</AuthButton>
              </>
            ) : (
              <>
                <AuthButton to="/MemberJoin">회원가입</AuthButton>
                <AuthButton to="/Login" $variant="primary">로그인</AuthButton>
              </>
            )}
          </MobileAuthGroup>
        </Nav>

        <RightGroup>
          {login ? (
            <>
              {(role === 'admin' || role === 'instructor') && (
                <AuthButton to="/Enroll?tab=manage">수강 승인</AuthButton>
              )}
              {role === 'admin' && (
                <AuthButton to="/Admin/DailyLectures">강의 관제</AuthButton>
              )}
              <UserChip to="/MyPage" title="마이페이지">
                <Avatar>{initial}</Avatar>
                <span>{name || user_id}</span>
                <RoleTag $role={role}>{roleLabel}</RoleTag>
              </UserChip>
              <LogoutTextBtn onClick={handleLogout}>로그아웃</LogoutTextBtn>
            </>
          ) : (
            <>
              <AuthButton to="/MemberJoin">회원가입</AuthButton>
              <AuthButton to="/Login" $variant="primary">로그인</AuthButton>
            </>
          )}
        </RightGroup>

        <Hamburger onClick={() => setMenuOpen(!menuOpen)} aria-label="메뉴 열기">
          <HamburgerLine $open={menuOpen} />
          <HamburgerLine $open={menuOpen} />
          <HamburgerLine $open={menuOpen} />
        </Hamburger>
      </HeaderInner>
    </HeaderOuter>
  );
}

export default Header;
